import { Controller } from 'stimulus';
import ManualRemovalPenTool from '@/js/editor/manual-removal-pen-tool';

const ZOOM_STEP = 0.25;
const RESET_ZOOM_KEYS = ['z', '0', '1'];
const ZOOM_IN_KEYS = ['=', '+'];
const DIGIT_ZOOM_KEYS = ['2', '3', '4', '5', '6', '7', '8', '9'];

export default class extends Controller {
  static targets = ['layout', 'zoomLevel', 'stage', 'preview', 'canvas'];
  static values = { zoom: { type: Number, default: 1 } };

  connect() {
    const shared = window.ed._shared;

    this.item = document.querySelector('.item.selected');
    shared.ensureImageData(this.item);

    const imageData =
      this.item.e.noBgImage && this.item.noBgImageData
        ? this.item.noBgImageData
        : this.item.originalImageData;

    this.penTool = new ManualRemovalPenTool(
      this.canvasTarget,
      imageData,
      this.item.e.backgroundRemovalCoordinates
    );
    this.previewTarget.style.backgroundImage = `url(${this.penTool.previewUrl})`;
    this.penTool.redraw();
  }

  disconnect() {
    this.penTool?.stopFlashing();
  }

  // The modal removes its own dialog after closing; the wrapper this controller sits on goes with it.
  layoutTargetDisconnected() {
    this.element.remove();
  }

  closed() {
    document.dispatchEvent(new CustomEvent('crop:cancel'));
  }

  press(event) {
    if (event.isPrimary) {
      this.penTool.press(event);
    }
  }

  move(event) {
    this.penTool.move(event);
  }

  release() {
    if (this.penTool.release()) {
      this.zoomValue = 1;
    }
  }

  scrolled() {
    this.penTool.redrawPaths();
  }

  zoomIn() {
    this.zoomValue += ZOOM_STEP;
  }

  zoomOut() {
    if (this.zoomValue > 1) {
      this.zoomValue -= ZOOM_STEP;
    }
  }

  keydown(event) {
    if (this.#open && (event.key === 'Meta' || event.key === 'Control')) {
      this.#redrawWithModifier(event);
    }
  }

  keyup(event) {
    if (!this.#open) {
      return;
    }

    this.#redrawWithModifier(event);

    if (RESET_ZOOM_KEYS.includes(event.key)) {
      this.zoomValue = 1;
    } else if (ZOOM_IN_KEYS.includes(event.key)) {
      this.zoomIn();
    } else if (event.key === '-') {
      this.zoomOut();
    } else if (DIGIT_ZOOM_KEYS.includes(event.key)) {
      this.zoomValue = parseInt(event.key);
    }
  }

  reset() {
    this.penTool.reset();
    this.penTool.redraw();
  }

  save({ currentTarget }) {
    if (this.penTool.hasOpenPath) {
      currentTarget.style.pointerEvents = 'none';
      this.penTool.flashOpenPath(() => (currentTarget.style.pointerEvents = ''));

      return;
    }

    window.ed.featureActionsService.add('left_tool_bar_bg_removed_manual');

    const shared = window.ed._shared;
    const { item } = this;
    const props = item.e;
    const paths = this.penTool.closedPaths;

    props.setManualBackgroundRemovalProps(paths.length ? paths : null);
    props.width = undefined;
    props.height = undefined;
    props.cropScale = undefined;

    shared
      .drawItem(item, props)
      .finally(() => shared.finishAddingItem({ item, props, selectItem: true }));

    currentTarget.closest('dialog')?.close();
  }

  zoomValueChanged(zoom) {
    if (!this.hasStageTarget) {
      return;
    }

    const stage = this.stageTarget;
    stage.style.overflow = 'auto';
    stage.scrollTop = 0;
    stage.scrollLeft = 0;
    stage.style.overflow = zoom > 1 ? 'scroll' : 'hidden';

    this.zoomLevelTarget.textContent = zoom * 100;
    this.canvasTarget.style.transform = `scale(${zoom})`;
    this.previewTarget.style.transform = `scale(${zoom})`;
  }

  #redrawWithModifier(event) {
    this.penTool.deleteModifier = event.metaKey || event.ctrlKey;
    this.penTool.redraw();
  }

  get #open() {
    return this.layoutTarget.closest('dialog')?.open;
  }
}
